import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { listRoles, type Role } from '@/services/admin.service';

/**
 * Legacy parity: role-title overrides (AR-3).
 *
 * Legacy `appointment-schedule.js` L6299-6324 reads `data-roleTitleDoctor`,
 * `data-roleTitleCoordinator`, and `data-roleTitleNurse` injected server-side
 * by PHP — the `aa_accounts_roles` rows the account edits under Admin →
 * Account Details. `listRoles()` exposes the same table to v5.
 *
 * This hook fetches roles once and returns a `Record<key, roleTitle>` ready to
 * merge into display labels. Falls back to the English defaults each call site
 * previously hard-coded when no matching role is configured.
 *
 * Keys: `aa_accounts_roles.roleType` only ever holds the singular capitalized
 * strings legacy `admin_role_save.php` writes — 'Doctor', 'Coordinator',
 * 'Nurse', 'Receptionist'. Call sites key off several conventions, though: the
 * scheduler uses the singular `resources.resourceType` ('doctor'), while
 * `RESOURCE_TYPE_TO_ROLE_KEY` maps that to a plural ('doctors'). Registering
 * every alias keeps a lookup from silently missing and falling back to the
 * English default — which is exactly what the plural-only map used to do.
 */

const DEFAULT_ROLE_TITLES: Record<string, string> = {
  doctors: 'Doctors',
  coordinators: 'Coordinators',
  nurses: 'Nurses',
  equipment: 'Equipment',
  rooms: 'Rooms',
  other: 'Other',
};

/** Every key a caller might look a role title up by, given its roleType. */
function aliasesFor(roleType: string): string[] {
  const raw = roleType.trim();
  if (raw === '') return [];
  const lower = raw.toLowerCase();
  // 'Doctor' -> ['Doctor', 'doctor', 'doctors']. Already-plural types (none
  // today, but custom rows are free-text) stay as-is rather than gaining an
  // 's' twice.
  const plural = lower.endsWith('s') ? lower : `${lower}s`;
  return Array.from(new Set([raw, lower, plural]));
}

/**
 * `aa_accounts_roles` has no `active` column — it soft-deletes via `endDate`
 * against the zero-date sentinel. The API synthesizes `active` from that, but
 * treat a missing flag as active so a stale/unmigrated API response degrades to
 * showing the configured title rather than to the English default.
 */
function isRoleActive(role: Role): boolean {
  return role.active !== false;
}

export interface UseRoleTitlesResult {
  titles: Record<string, string>;
  isLoading: boolean;
  /** Active roles only — soft-deleted rows are dropped. */
  roles: Role[];
}

export function useRoleTitles(): UseRoleTitlesResult {
  const query = useQuery<Role[]>({
    queryKey: ['roles'],
    queryFn: listRoles,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const roles = useMemo<Role[]>(
    () => (query.data ?? []).filter(isRoleActive),
    [query.data],
  );

  const titles = useMemo<Record<string, string>>(() => {
    const overrides: Record<string, string> = { ...DEFAULT_ROLE_TITLES };
    for (const role of roles) {
      const title = (role.roleTitle ?? '').trim();
      if (title.length === 0) continue;
      for (const key of aliasesFor(role.roleType ?? '')) {
        overrides[key] = title;
      }
    }
    return overrides;
  }, [roles]);

  return {
    titles,
    isLoading: query.isLoading,
    roles,
  };
}
